import type {
  EligibilityCurrentResponse,
  EligibilityFieldProvenance,
} from '@features/patient-coverages/types';

import { FINANCIAL_FIELDS } from './verificationFinancials';

export function coverageSourceLabel(source: EligibilityFieldProvenance): string {
  if (source.legacy_basis === 'unresolved') return 'Earlier history · source uncertain';
  if (source.kind === 'manual')
    return source.method === 'portal'
      ? 'Payer portal'
      : source.method === 'phone'
        ? 'Phone confirmation'
        : 'Manual record';
  if (source.kind === 'automatic') return 'JotPsych check';
  if (source.kind === 'accept_copay') return 'Accepted payer evidence';
  if (source.kind === 'keep_current') return 'Retained current benefit';
  return 'Earlier history';
}

export function coverageContactTime(value: string | null | undefined): string {
  if (!value || !Number.isFinite(new Date(value).getTime()))
    return 'Original contact time unavailable';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(value));
}

/** A shared source is claimed only when every populated/asserted field agrees. */
export function coverageSourceSummary(current: EligibilityCurrentResponse): {
  badge: string;
  label: string;
  source?: EligibilityFieldProvenance;
} {
  const fields = ['coverage_result', ...FINANCIAL_FIELDS.map(field => field.apiKey)] as const;
  const sources: EligibilityFieldProvenance[] = [];
  let missing = false;
  for (const field of fields) {
    const source = current.provenance?.[field];
    const value = field === 'coverage_result' ? current.coverage_result : current.benefits?.[field];
    if (source) sources.push(source);
    else if (value != null && value !== 'unknown') missing = true;
  }
  if (!sources.length) return { badge: 'Source unavailable', label: 'Source details unavailable' };
  const first = sources[0];
  if (sources.some(source => JSON.stringify(source) !== JSON.stringify(first)))
    return { badge: 'Multiple sources', label: 'Multiple sources · inspect individual benefits' };
  if (missing)
    return { badge: 'Source details incomplete', label: 'Inspect individual benefit sources' };
  return {
    badge:
      first.kind === 'manual'
        ? 'Manual record'
        : first.kind === 'automatic'
          ? 'Automatic check'
          : 'Earlier history',
    label: coverageSourceLabel(first),
    source: first,
  };
}
